import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useErrorHandler, ApiError } from './useErrorHandler';
import { useOfflineDetection } from './useOfflineDetection';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T = any>() {
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const { signOut } = useAuth();
  const { executeWithRetry, handleError, isRetrying, retryCount } = useErrorHandler();
  const { isOffline } = useOfflineDetection();

  const execute = useCallback(async (
    apiCall: () => Promise<{ data?: T; error?: ApiError }>,
    enableRetry: boolean = true
  ): Promise<T | null> => {
    // Check if offline
    if (isOffline) {
      setState({
        data: null,
        loading: false,
        error: 'You are currently offline. Please check your internet connection.',
      });
      return null;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const executeFunction = enableRetry ? executeWithRetry : 
        async (op: () => Promise<{ data?: T; error?: ApiError }>) => await op();

      const response = await executeFunction(apiCall);

      if (response.error) {
        // Handle authentication errors by signing out
        if (response.error.code === 'AUTH_REQUIRED' || 
            response.error.code === 'UNAUTHORIZED' ||
            response.error.code === 'TOKEN_EXPIRED') {
          await signOut();
          setState({
            data: null,
            loading: false,
            error: 'Your session has expired. Please sign in again.',
          });
          return null;
        }

        const errorMessage = handleError(response.error);
        setState({
          data: null,
          loading: false,
          error: errorMessage,
        });
        return null;
      }

      setState({
        data: response.data || null,
        loading: false,
        error: null,
      });

      return response.data || null;
    } catch (error: any) {
      console.error('API call failed:', error);
      setState({
        data: null,
        loading: false,
        error: error.message || 'An unexpected error occurred',
      });
      return null;
    }
  }, [signOut, executeWithRetry, handleError, isOffline]);

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
    });
  }, []);

  return {
    ...state,
    execute,
    reset,
    isRetrying,
    retryCount,
    isOffline,
  };
}

// Specialized hooks for common patterns
export function useApiMutation<T = any>() {
  const { execute, loading, error, reset, isRetrying, retryCount, isOffline } = useApi<T>();

  const mutate = useCallback(async (
    apiCall: () => Promise<{ data?: T; error?: ApiError }>,
    enableRetry: boolean = true
  ): Promise<boolean> => {
    const result = await execute(apiCall, enableRetry);
    return result !== null;
  }, [execute]);

  return {
    mutate,
    loading,
    error,
    reset,
    isRetrying,
    retryCount,
    isOffline,
  };
}

export function useApiQuery<T = any>(
  apiCall: (() => Promise<{ data?: T; error?: ApiError }>) | null,
  dependencies: any[] = [],
  enableRetry: boolean = true
) {
  const { data, loading, error, execute, reset, isRetrying, retryCount, isOffline } = useApi<T>();
  const [hasInitialLoad, setHasInitialLoad] = React.useState(false);
  
  // Use ref to store stable reference to apiCall to prevent infinite loops
  const apiCallRef = React.useRef(apiCall);
  apiCallRef.current = apiCall;

  const refetch = useCallback(() => {
    if (apiCallRef.current) {
      return execute(apiCallRef.current, enableRetry);
    }
    return Promise.resolve(null);
  }, [execute, enableRetry]);

  // Auto-execute on mount only (no dependency on apiCall to prevent infinite loops)
  React.useEffect(() => {
    if (apiCallRef.current && !hasInitialLoad) {
      // Add a small delay to prevent simultaneous API calls
      const timeoutId = setTimeout(() => {
        execute(apiCallRef.current!, enableRetry);
        setHasInitialLoad(true);
      }, Math.random() * 100); // Random delay between 0-100ms to stagger calls

      return () => clearTimeout(timeoutId);
    }
  }, [execute, enableRetry, hasInitialLoad]); // Removed apiCall from dependencies

  // Handle dependency changes (but not on initial load)
  React.useEffect(() => {
    if (apiCallRef.current && hasInitialLoad) {
      execute(apiCallRef.current, enableRetry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  return {
    data,
    loading,
    error,
    refetch,
    reset,
    isRetrying,
    retryCount,
    isOffline,
  };
}

export default useApi;
